import { getProjectedDate } from "@utils/utils";
import { useCallback, useEffect } from "react";

import { Paragraph, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTargetGoalBankTimelineEstimationTypeChoices,
  ShortTermTimelineEstimationEnums,
  TargetTimelineEstimationEnums,
  UpdateGoalBankTargetInput,
  useUpdateGoalBankTargetMutation,
} from "@api/graphql/types-and-hooks";
import { useGetShortTermGoals } from "@components/GoalsBank/hooks/useGetShortTermGoals";
import { renderGoalBankFormDialog } from "@components/GoalsBank/utils";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { convertDBString, convertReadableString } from "@components/forms/utils";
import { notifySuccess } from "@components/notifications/notifications";

export interface TargetFormProps {
  id: string;
  masteryCriteria?: string;
  medicalRationale?: string;
  goalBank: {
    id: string;
    goalName: string;
  };
  targetName: string;
  timelineEstimationType?: TargetTimelineEstimationEnums | ApiReportTargetGoalBankTimelineEstimationTypeChoices;
  timelineEstimationValue?: number;
}

interface GoalBankTargetsEditFormProps {
  folderId: string;
  refetchGoals: () => void;
  selectedGoalBankTarget: TargetFormProps;
  openEditForm: boolean;
  setOpenEditForm: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GoalBankTargetsEditForm: React.FC<GoalBankTargetsEditFormProps> = ({
  refetchGoals,
  selectedGoalBankTarget,
  openEditForm,
  setOpenEditForm,
  folderId,
}) => {
  const updateTargets = useUpdateGoalBankTargetMutation({});

  const handleSubmit = useCallback(
    async (params: UpdateGoalBankTargetInput) => {
      try {
        await updateTargets.mutateAsync({
          input: {
            goalBankTargetId: selectedGoalBankTarget?.id,
            masteryCriteria: params?.masteryCriteria,
            medicalRationale: params?.medicalRationale,
            goalBankId: params?.goalBankId,
            targetName: params?.targetName,
            timelineEstimationValue: params?.timelineEstimationValue ? Number(params?.timelineEstimationValue) : null,
            timelineEstimationType: params?.timelineEstimationType
              ? (convertDBString(params?.timelineEstimationType) as TargetTimelineEstimationEnums)
              : null,
          },
        });
        refetchGoals();
        notifySuccess("Successfully Updated Target");
        setOpenEditForm(false);
      } catch (error) {
        console.error("Error when saving target", error);
      }
    },
    [refetchGoals, updateTargets, selectedGoalBankTarget, setOpenEditForm],
  );

  const { formState, watch, RegisteredFormInput, RegisteredFormSelected, reset, onSubmit } =
    useFormUtils<UpdateGoalBankTargetInput>({
      mutationFn: handleSubmit,
    });

  useEffect(() => {
    if (selectedGoalBankTarget) {
      reset({
        goalBankTargetId: selectedGoalBankTarget?.id,
        masteryCriteria: selectedGoalBankTarget?.masteryCriteria,
        medicalRationale: selectedGoalBankTarget?.medicalRationale,
        goalBankId: selectedGoalBankTarget?.goalBank?.id,
        targetName: selectedGoalBankTarget?.targetName,
        timelineEstimationValue: selectedGoalBankTarget?.timelineEstimationValue,
        timelineEstimationType: convertReadableString(
          selectedGoalBankTarget?.timelineEstimationType,
        ) as TargetTimelineEstimationEnums,
      });
    }
  }, [reset, selectedGoalBankTarget]);

  const { shortTermGoals } = useGetShortTermGoals(folderId);

  const duration = watch("timelineEstimationValue");
  const durationType = watch("timelineEstimationType");

  const dialog = renderGoalBankFormDialog({
    title: "Edit Target",
    openForm: openEditForm,
    setOpenForm: setOpenEditForm,
    onSubmit: onSubmit,
    children: (
      <FormContainer onSubmit={onSubmit}>
        <RegisteredFormInput required formKey="targetName" formState={formState} label="Target Name" />
        <RegisteredFormSelected
          required={true}
          formKey="goalBankId"
          formState={formState}
          items={
            (shortTermGoals?.getGoalBankGoals?.edges.map((edge) => {
              return {
                id: edge?.node?.id,
                primary: edge?.node?.goalName,
                className: "ph-no-capture",
              };
            }) as SelectItem[]) || []
          }
          title="Goal"
          placeholderText={"Select Goal"}
        />
        <RegisteredFormInput
          required={false}
          formKey="masteryCriteria"
          formState={formState}
          label="Mastery Criteria"
        />
        <Paragraph displayType="loud" colorType="primary">
          Expected Mastery Date
        </Paragraph>
        <div className="w-full flex items-center justify-start mt-2 gap-x-4">
          <RegisteredFormSelected
            required={false}
            formKey="timelineEstimationValue"
            formState={formState}
            items={
              Array.from({ length: 60 }, (_, index) => ({
                primary: `${index + 1}`,
              })) as unknown as SelectItem[]
            }
            title="Duration"
            placeholderText={"Select Duration"}
            className="w-full"
          />
          <RegisteredFormSelected
            required={false}
            formKey="timelineEstimationType"
            formState={formState}
            items={
              Object.values(ShortTermTimelineEstimationEnums).map((o) => ({
                primary: convertReadableString(o),
              })) as SelectItem[]
            }
            title="Time Period"
            className="w-full"
            placeholderText={"Select Time Period"}
          />
        </div>
        {duration && durationType && (
          <div>
            Expected Mastery Date:{" "}
            {getProjectedDate(Number(duration), durationType.toLowerCase() as "days" | "weeks" | "months")}
          </div>
        )}
      </FormContainer>
    ),
  });

  return <>{dialog}</>;
};
